import * as React from 'react';

/**
 * @namespace UIWizard
 */

/**
 * @typedef SetLabelFunction
 * @type {Function}
 * @param {string} value
 * 
 */

/**
 * @typedef OnChangeValue
 * @type {Object}
 * @property {any} data
 * @property {any} value
 */

/**
 * @typedef ChangeFunction
 * @type {Function}
 * @param {OnChangeValue} event
 */

/**
 * @typedef {Object} RunActionEvent
 * @property {string} data The registered type of the action to run
 * @property {any} value An optional arbitrary value that will be passed as the second argument to the action's `perform` function
 */

/**
 * @typedef {Function} RunActionFunction
 * @param {RunActionEvent} actionEvent The event object specifying the type of action to run and an optional value parameter to pass to the action's `perform` function.
 */

/**
 * @typedef {Object} TWizardContext
 * @property {Object} activeStep The model for the current WizardStep.
 * @property {string} backText The label for the current 'Back' button.
 * @property {any} context The value that is passed to the Wizard component as the "context" property. This property is designed as a means to pass arbitrary context from the surrounding environment to the Wizard itself.
 * @property {Object} model The wizard model used to render steps and configure next and back buttons
 * @property {boolean} nextDisabled Whether the current next step is disabled or not
 * @property {string} nextText The label for the current 'Next' button.
 * @property {ChangeFunction} onChange This function can be called with a string as the 'data' property and any value as the 'value' property in order to update wizardData.
 * @property {Function} onReset This function will reset the wizard's step state and if an onReset function has been passed to the Wizard component, it will also be called.
 * @property {RunActionFunction} runAction Call this function with a type string for a registered 'WizardAction' plugin to call it with the current WizardContext.
 * @property {Function} setActiveStep Call this function with the key of a step within the wizard model's 'steps' object to navigate to that step.
 * @property {Function} setHeader Called with a string value to set the header text for the current step.
 * @property {Function} setHeaderIcon Called with a string value to set the header icon for the current step.
 * @property {Function} setNextDisabled Function used to disable the current 'Next' button.
 * @property {SetLabelFunction} setNextText Function used to set the label for the current 'Next' button. This value is reset each time a step changes.
 * @property {SetLabelFunction} setBackText Function used to set the label for the current 'Back' button. This value is reset each time a step changes.
 * @property {any} wizardData The value passed to the wizardComponent as the 'value' property.
 * @property {string} wizardId A unique string that identifies the current wizard in the event that multiple are launched at once.
 * 
 */

/**
 * @type {TWizardContext}
 * @description The wizard context, which is shared among all child elements of the wizard. Provides helpful methods for manipulating the wizard's state, including the current displayed
 * step, the header title, header icon, next button and back button text as well as the button disabled states. If value and onChange properties are supplied to the parent Wizard component, 
 * the "value" of the wizard can also be manipulated using the onChange function.
 */
const WizardContext = /*#__PURE__*/React.createContext(null);
export default WizardContext;